import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BASE_URL } from "../config";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authToken, setauthToken] = useState(true);
  const [permissions, setPermissions] = useState([]); // Store permissions
  const [scopes, setScopes] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  const normalizeScopes = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value.map((scope) => String(scope).trim()).filter(Boolean);
    }
    if (typeof value === "string") {
      return value
        .split(/[\s,]+/)
        .map((scope) => scope.trim())
        .filter(Boolean);
    }
    return [];
  };

  const validateToken = async () => {
    const token = localStorage.getItem("authentication/token");
    const refreshToken = localStorage.getItem("authentication/refresh_token");
    let savedPermissions = [];
    let savedScopes = [];

    //   may error kapag na tampered yung permissions nang other value
    // const savedPermissions = JSON.parse(
    //   localStorage.getItem("authentication/permissions") || "[]"
    // );
    // Safely parse and validate the permissions
    try {
      const rawPermissions = localStorage.getItem("authentication/permissions");
      const parsedPermissions = JSON.parse(rawPermissions);

      if (Array.isArray(parsedPermissions)) {
        savedPermissions = parsedPermissions;
      } else {
        console.warn("Invalid permissions format. Resetting to empty array.");
        localStorage.removeItem("authentication/permissions");
      }
    } catch (error) {
      console.error("Error parsing permissions:", error);
      localStorage.removeItem("authentication/permissions");
    }
    // Safely parse and validate the permissions

    const rawScopes = localStorage.getItem("authentication/scopes");
    savedScopes = normalizeScopes(rawScopes);

    if (!token) {
      setIsLoading(false);
      return false;
    }

    try {
      const response = await fetch(`${BASE_URL}/validate-token`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

        if (response.ok) {
        setauthToken(localStorage.getItem("authentication/token"));
        setPermissions(savedPermissions);
        setScopes(savedScopes);
        setIsAuthenticated(true);
        if (location.pathname === "/" || location.pathname === "/login") {
          navigate("/dashboard", { replace: true });
        }
        return true;
      } else if (refreshToken) {
        await refreshAccessToken(refreshToken);
      } else {
        setIsAuthenticated(false);
        if (location.pathname !== "/login") {
          navigate("/login", { replace: true });
        }
      }
    } catch (error) {
      console.error("Token validation failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshAccessToken = async (refreshToken) => {
    try {
      const response = await fetch(`${BASE_URL}/refresh-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (response.ok) {
        const result = await response.json();
       localStorage.setItem("isLoggedIn", "true");
       localStorage.setItem("authentication/token", result.access_token);
       localStorage.setItem("authentication/expire", result.expires_in);
       localStorage.setItem("authentication/scopes", result.scope);
       localStorage.setItem(
         "authentication/refresh_token",
         result.refresh_token
       );
       localStorage.setItem("authentication/token_type", result.token_type);
       localStorage.setItem(
         "authentication/permissions",
         JSON.stringify(result.permissions)
       );

          setauthToken(localStorage.getItem("authentication/token"));
        setPermissions(Array.isArray(result.permissions) ? result.permissions : []);
        setScopes(normalizeScopes(result.scope));
        setIsAuthenticated(true);
        if (location.pathname === "/" || location.pathname === "/login") {
          navigate("/dashboard", { replace: true });
        }
      } else {
        localStorage.clear();
        setIsAuthenticated(false);
        navigate("/login", { replace: true });
        return false;
      }
    } catch (error) {
      localStorage.clear();
      navigate("/login", { replace: true });
      return false;
    }
  };

  const hasPermission = (requiredPermission) => {
    return permissions.includes(requiredPermission); // Check if permission exists
  };

  const hasScope = (requiredScopes) => {
    if (!requiredScopes || requiredScopes.length === 0) return true;
    const normalized = normalizeScopes(requiredScopes);
    if (normalized.length === 0) return true;
    if (scopes.includes("system:all")) return true;
    return normalized.some((scope) => scopes.includes(scope));
  };

  useEffect(() => {
    validateToken();
  }, []);

  return (
    <AuthContext.Provider
          value={{
              isAuthenticated,
              isLoading,
              permissions,
              hasPermission,
              scopes,
              hasScope,
              authToken 
          }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for using AuthContext
export const useAuth = () => {
  return useContext(AuthContext);
};
